/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-assignment-of-untracked-properties-used-in-tracking-contexts, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import Atsi from '@linc-technologies/linced-common/enums/au-atsi';
import EmberObject, { get, action, computed } from '@ember/object';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import NSWStatus from '@linc-technologies/linced-common/enums/nsw-status';
import moment from 'moment';
import { setDiff, or, alias } from '@ember/object/computed';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { observes } from '@ember-decorators/object';
import { tracked } from '@glimmer/tracking';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';

// collator is configured to enable natural string sorting, so '1', '10', '2'
// doesn't happen.
const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});
const genderAllLabel = 'All';
const genderMaleLabel = 'Male';
const genderFemaleLabel = 'Female';

/**
 * chartRow provides the base object in order to generate a row of data for
 * the absences and truancy report by counting up the number of daily truancy
 * codes.
 */
class chartRow extends EmberObject {
  init() {
    super.init(...arguments);

    // Attrs
    this.totalCodes = 0;
    this.studentPresentPercents = [];
    // L contains the sum of periods for which the matching students have been late.
    this.L = 0;
  }

  // Methods
  /**
   * percentageBracketMap returns a map holding the percentages of students in
   * each attendance bracket.
   */
  @computed('studentPresentPercents.[]')
  get percentageBracketMap() {
    let totalStudents = this.studentPresentPercents.length;
    let counts = {};
    this.studentPresentPercents.forEach((studentPercent) => {
      if (studentPercent <= 0.7) {
        counts['70'] = (counts['70'] || 0) + 1;
      } else if (studentPercent > 0.7 && studentPercent <= 0.8) {
        counts['80'] = (counts['80'] || 0) + 1;
      } else if (studentPercent > 0.8 && studentPercent <= 0.9) {
        counts['90'] = (counts['90'] || 0) + 1;
      } else if (studentPercent > 0.9) {
        counts['100'] = (counts['100'] || 0) + 1;
      }
    });
    return {
      P70: this._toPercent(counts['70'], totalStudents),
      P80: this._toPercent(counts['80'], totalStudents),
      P90: this._toPercent(counts['90'], totalStudents),
      P100: this._toPercent(counts['100'], totalStudents),
    };
  }

  @alias('percentageBracketMap.P70')
  P70;

  @alias('percentageBracketMap.P80')
  P80;

  @alias('percentageBracketMap.P90')
  P90;

  @alias('percentageBracketMap.P100')
  P100;

  @computed('studentPresentPercents.[],L')
  get late() {
    return this._toPercent(this.L, this.totalCodes);
  }

  /**
   * _toPercent calculates a percentage to 2dp.
   *
   * @param {number} numerator - provides a subset of the counted codes.
   * @param {number} denominator - provides the sum of all counted codes in
   *    order to derive a percentage.
   * @returns {string}
   * @private
   */
  _toPercent(numerator, denominator) {
    if (denominator === 0 || isEmpty(numerator)) {
      return '0.00';
    }

    return ((numerator / denominator) * 100).toFixed(2);
  }
}

/**
 * chartData contains the data structure for processing and storing the
 * calculated chart information.
 */
class chartData extends EmberObject {
  init() {
    super.init(...arguments);

    // Attrs
    // schoolRates contains a flat row of data for whole school counts.
    this.schoolRates = chartRow.create();
    // yearLevelRates contains an object mapping:
    // { 'Year Level': {chartRow}, ... }.
    this.yearLevelRates = {};
    // jurisdictionalEthnicityRates contains an object mapping:
    // { 'Ethnicity/ATSI': {chartRow}, ... }.
    this.jurisdictionalEthnicityRates = {};
    // genderRates contains an object mapping:
    // { 'Gender Label': {chartRow}, ... }.
    this.genderRates = {};
    // yearLevelGenderEthnicRates contains an object mapping:
    // { 'Year Level': { 'Gender Label': {chartRow}, ... }, ... }
    this.yearLevelGenderEthnicRates = {};
  }

  // Methods
  /**
   * ethnicities returns a sorted array of ethnicity display names.
   */
  @computed('jurisdictionalEthnicityRates')
  get ethnicities() {
    return Object.keys(this.jurisdictionalEthnicityRates).sort();
  }

  /**
   * yearLevels returns a sorted array of year level display names.
   */
  @computed('yearLevelRates')
  get yearLevels() {
    return Object.keys(this.yearLevelRates).sort(collator.compare);
  }

  /**
   * genders returns an array of sorted genders.
   */
  @computed('genderRates')
  get genders() {
    return Object.keys(this.genderRates).sort();
  }

  /**
   * genderLabels returns an array of sorted gender labels, including 'all'.
   */
  @computed('genderRates')
  get genderLabels() {
    let labels = Object.keys(this.genderRates).sort();
    labels.unshift(genderAllLabel);

    return labels;
  }

  /**
   * yearLevelGenderEthnic returns a sorted array of year levels.
   */
  @computed('yearLevelGenderEthnicRates')
  get yearLevelGenderEthnic() {
    return Object.keys(this.yearLevelGenderEthnicRates).sort(collator.compare);
  }
}

export default class AbsentRateAnalysisReportController extends Controller {
  queryParams = ['dateFrom', 'dateTo', 'groupIds', 'termId'];

  // Attrs
  level3Ethnicities = [
    'Māori',
    'Tokelauan',
    'Fijian',
    'Niuean',
    'Tongan',
    'Cook Islands Māori',
    'Samoan',
    'Other Pacific Peoples',
    'Filipino',
    'Cambodian',
    'Vietnamese',
    'Other Southeast Asian',
    'Indian',
    'Chinese',
    'Sri Lankan',
    'Japanese',
    'Korean',
    'Other Asian',
    'Middle Eastern',
    'Latin American',
    'African',
    'Other Ethnicity',
    'Not Stated',
    'Australian',
    'British/Irish',
    'German',
    'Dutch',
    'Greek',
    'Polish',
    'South Slav',
    'Italian',
    'Other European',
    'NZ European/Pākehā',
  ];

  @computed('isNZSchool')
  get title() {
    if (this.isNZSchool) {
      return `Absent Rate Analysis Report (1/2 day calc code)`;
    }
    return `Absent Rate Analysis Report (calculated day code)`;
  }

  @computed('isNZSchool', 'isNSWSchool')
  get jurisdictionalEthnicFilters() {
    if (this.isNZSchool) {
      return this.level3Ethnicities;
    } else if (this.isNSWSchool) {
      return Atsi.values.map((atsi) => atsi.label);
    } else {
      // Default to NZMoE
      return this.level3Ethnicities;
    }
  }

  @computed('model.genderField.options.[]')
  get genders() {
    let options = this.get('model.genderField.options');
    if (!isEmpty(options)) {
      // Use options directly from the gender field.
      return options;
    }
    return [
      { value: 'M', label: 'Male' },
      { value: 'F', label: 'Female' },
    ];
  }

  genderAllLabel = genderAllLabel;
  genderMaleLabel = genderMaleLabel;
  genderFemaleLabel = genderFemaleLabel;

  @alias('session.school.yearLevels')
  yearLevels;

  // Services
  @service
  attendance;

  @service
  attendanceCodes;

  @service
  session;

  @service
  store;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get isNZSchool() {
    return this.jurisdiction === Jurisdiction.NZ_MOE;
  }

  @computed('jurisdiction')
  get isNSWSchool() {
    return this.jurisdiction === Jurisdiction.AU_NSW;
  }

  @or('isNZSchool', 'isNSWSchool')
  showEthnicities;

  @computed('isNZSchool', 'isNSWSchool')
  get ethnicityLabel() {
    if (this.isNZSchool) {
      return 'Ethnicity';
    } else if (this.isNSWSchool) {
      return 'ATSI';
    } else {
      return 'Ethnicity';
    }
  }

  // Props
  // dateFrom provides the stateful binding to a query param.
  dateFrom = `${moment().year()}-01-01`;

  // dateTo provides the stateful binding to a query param.
  dateTo = `${moment().year()}-12-31`;

  // State
  // newDateFrom enables the user to enter a date without it immediately
  // updating query params as soon as the date appears "valid", i.e. when the
  // first digit of the year is entered.
  @tracked newDateFrom = `${moment().year()}-01-01`;

  // newDateTo enables the user to enter a date without it immediately updating
  // query params as soon as the date appears "valid", i.e. when the first digit
  // of the year is entered.
  @tracked newDateTo = `${moment().year()}-12-31`;

  // ethnicityFilter stores the currently selected ethnicities to filter
  // learners by.
  @tracked ethnicityFilter = [];

  // genderFilter stores the currently selected genders to filter learners
  // by.
  @tracked genderFilter = [];

  // groupFilter stores the currently selected groups to filter learners
  // by.
  @tracked groupIds = [];

  @computed('groupIds.[],model.groups.[]')
  get groupFilter() {
    return this.groupIds.map((id) => {
      return this.store.peekRecord('group', id);
    });
  }

  // yearLevelFilter stores the currently selected year levels to filter
  // learners by.
  @tracked yearLevelFilter = [];

  // remainingEthnicities calculates what Ethnicities are still eligible to be
  // selected in order to stop duplication of filters.
  @setDiff('jurisdictionalEthnicFilters', 'ethnicityFilter')
  remainingEthnicities;

  // remainingGenders calculates what Genders are still eligible to be selected
  // in order to stop duplication of filters.
  @setDiff('genders', 'genderFilter')
  remainingGenders;

  // remainingGroups calculates what Groups are still eligible to be selected in
  // order to stop duplication of filters.
  @setDiff('filteredGroups', 'groupFilter')
  remainingGroups;

  // remainingYearLevels calculates what YearLevels are still eligible to be
  // selected in order to stop duplication of filters.
  @setDiff('yearLevels', 'yearLevelFilter')
  remainingYearLevels;

  // Methods
  /**
   * truancyCodeMap generates a map of attendance codes to their respective
   * truancy codes.
   */
  @computed('attendanceCodes.allCodes.@each.{code,truancy}')
  get truancyCodeMap() {
    let codes = this.get('attendanceCodes.allCodes');
    if (isEmpty(codes)) {
      return {};
    }
    return codes.reduce((map, code) => {
      map[code.code] = code.truancy;
      return map;
    }, {});
  }

  /**
   * hasOverseasTruancyCode determines whether the school has any attendance
   * codes which map to a truancy code of "overseas".
   */
  @computed('attendanceCodes.allCodes.@each.truancy')
  get hasOverseasTruancyCode() {
    let codes = this.get('attendanceCodes.allCodes');
    if (isEmpty(codes)) {
      return false;
    }
    return !isEmpty(codes.find((item) => item.truancy === 'O'));
  }

  /**
   * filteredLearners returns an array of learners, filtered by date, year
   * level, gender and groups as specified by the user.
   */
  @computed(
    'model.learners.[]',
    'ethnicityFilter.[]',
    'genderFilter.[]',
    'groupFilter.[]',
    'yearLevelFilter.[]',
  )
  get filteredLearners() {
    if (
      isEmpty(this.ethnicityFilter) &&
      isEmpty(this.genderFilter) &&
      isEmpty(this.groupFilter) &&
      isEmpty(this.yearLevelFilter)
    ) {
      return this.model.learners;
    }

    let hasEthnicities = !isEmpty(this.ethnicityFilter);
    let hasGenders = !isEmpty(this.genderFilter);
    let hasGroups = !isEmpty(this.groupFilter);
    let hasYearLevels = !isEmpty(this.yearLevelFilter);
    let genderFilter = this.genderFilter.map((item) => item.value);

    return this.model.learners.filter((person) => {
      let include = true;
      if (hasGroups) {
        include = this.groupFilter.some((group) => {
          return group.currentLearners.find(
            (item) => item.personId === person.id,
          );
        });
      }
      if (include && hasEthnicities) {
        include = this.ethnicityFilter.includes(person.jurisdictionalEthnicity);
      }
      if (include && hasGenders) {
        include = genderFilter.includes(person.get('gender'));
      }
      if (include && hasYearLevels) {
        // Stringify learner's current year level, as the filter array contains
        // strings.
        include = this.yearLevelFilter.includes(
          '' + person.get('currentYearLevel'),
        );
      }
      return include;
    });
  }

  /**
   * sortedGroups returns groups sorted by type, then name.
   */
  @computed('model.groups.[]')
  get sortedGroups() {
    return this.model.groups
      .slice()
      .sort(
        (a, b) => a.type - b.type || (a.name || '').localeCompare(b.name || ''),
      );
  }

  /**
   * filteredGroups returns the groups as filtered by the user.
   */
  @computed('sortedGroups.[]')
  get filteredGroups() {
    return this.sortedGroups
      .filter((group) => {
        if (group.type === GroupType.GROUP) {
          // Omit My Groups which the current user isn't a current staff member in.
          let staff = group.get('currentStaff') ?? [];
          return !!staff.find(
            (item) => item.personId === this.session.personId,
          );
        }
        if (group.status === NSWStatus.INACTIVE) {
          return false;
        }
        // Include all other groups.
        return true;
      })
      .sort(byKeys('type', 'label'));
  }

  /**
   * chartingData calculates and returns the processed chart information ready
   * for display.
   */
  @computed(
    'model.dayCodes.[]',
    'groupFilter.[]',
    'yearLevelFilter.[]',
    'ethnicityFilter.[]',
    'genderFilter.[]',
    'genders.[]',
    'yearLevels.[]',
  )
  get chartingData() {
    // Initialize maps
    let codesByPerson = {};

    // Build required map based on jurisdiction
    codesByPerson = this.model.dayCodes.reduce((map, codes) => {
      map[codes.person_id] = codes;
      return map;
    }, {});

    let data = chartData.create();

    // Calculate attendance rates for each learner.
    this.filteredLearners.forEach((learner) => {
      let codes;
      codes = codesByPerson[learner.id] || [];

      if (isEmpty(codes)) {
        return;
      }

      let totalCount = 0;
      for (let code in codes.counts) {
        // Exclude exam leave from calculation
        if (code === 'X') {
          continue;
        }
        totalCount += codes.counts[code];
      }

      let presentCount = codes.counts['P'] || 0;

      if (totalCount !== 0) {
        data.schoolRates.studentPresentPercents.push(presentCount / totalCount);
      } else {
        data.schoolRates.studentPresentPercents.push(0);
      }

      data.schoolRates.totalCodes += totalCount;
      data.schoolRates.L += codes.lateCount;

      // Fetch the learners current year level in order to increment the
      // attendance rate counts based on year level.
      let currentYearLevel = learner.currentYearLevel;
      if (!isEmpty(currentYearLevel)) {
        data = this._incrementDataByField(
          data,
          codes,
          'yearLevelRates',
          `Year ${currentYearLevel}`,
        );
      }

      // Fetch the ethnicity (rankedL3 for NZ, ATSI for NSW) in order to increment the
      // attendance rate counts.
      let jurisdictionalEthnicity = learner.jurisdictionalEthnicity;
      if (!isEmpty(jurisdictionalEthnicity)) {
        data = this._incrementDataByField(
          data,
          codes,
          'jurisdictionalEthnicityRates',
          jurisdictionalEthnicity,
        );
      }

      // Fetch the learner's jurisdictional gender in order to increment the
      // gender based attendance rates.
      let genderField = 'fields.nzMOE:gender';
      if (this.jurisdiction !== Jurisdiction.NZ_MOE) {
        genderField = 'fields.core:genderLegal';
      }
      let genderCode = learner.get(`${genderField}.value`);
      let genderLabel = genderFemaleLabel;
      if (genderCode === 'M') {
        genderLabel = genderMaleLabel;
      }
      let genderOption = this.genders.find((item) => item.value === genderCode);
      if (!isEmpty(genderOption)) {
        genderLabel = genderOption.label;
      }
      if (!isEmpty(genderCode)) {
        data = this._incrementDataByField(
          data,
          codes,
          'genderRates',
          genderLabel,
        );
      }

      if (
        !isEmpty(currentYearLevel) &&
        !isEmpty(jurisdictionalEthnicity) &&
        !isEmpty(genderCode)
      ) {
        // Calculate the overall year level rates, split by gender, then
        // jurisdictionalEthnicity.
        data = this._incrementYearLevelGenderEthnicRates(
          data,
          codes,
          `Year ${currentYearLevel}`,
          genderAllLabel,
          jurisdictionalEthnicity,
        );

        data = this._incrementYearLevelGenderEthnicRates(
          data,
          codes,
          `Year ${currentYearLevel}`,
          genderLabel,
          jurisdictionalEthnicity,
        );
      }
    });
    return data;
  }

  /**
   * selectedTerm returns the user selected term based on the term id.
   */
  @computed('termId', 'model.terms.[]')
  get selectedTerm() {
    return this.model.terms.find((item) => item.id === this.termId);
  }

  /**
   * updateDates should be run debounced in order to prevent the route's model
   * reloading as soon as a "valid" date is entered, as the first digit of the
   * year will make it "valid".
   */
  updateDates() {
    let props = {
      termId: '',
    };
    let hasNewValue = false;

    if (!isEmpty(this.newDateFrom)) {
      props.dateFrom = this.newDateFrom;
      props.newDateFrom = null;
      hasNewValue = true;
    }

    if (!isEmpty(this.newDateTo)) {
      props.dateTo = this.newDateTo;
      props.newDateTo = null;
      hasNewValue = true;
    }

    if (hasNewValue) {
      this.setProperties(props);
    }
  }

  /**
   * _datesChanged updates the "new" date properties to keep them in line with
   * the query params.
   */
  @observes('dateFrom', 'dateTo')
  _datesChanged() {
    this.newDateFrom = this.dateFrom;
    this.newDateTo = this.dateTo;
  }

  /**
   * _incrementDataByField adds a count to a bucket based on the calculated
   * day code.
   *
   * @param {chartData} data - the chart data object to mutate.
   * @param {codes} codes - contains a map of codes to counts.
   * @param {string} chartField - the object key to be inserting data for, for
   *    example, "yearLevelRates".
   * @param {string} learnerFieldValue - the object key to be inserting data for
   *    for example, "Year 1"
   * @returns {chartData} - the processed chart data object.
   * @private
   */
  _incrementDataByField(data, codes, chartField, learnerFieldValue) {
    if (!isEmpty(learnerFieldValue)) {
      let chartFieldRates = get(data, `${chartField}.${learnerFieldValue}`);
      if (isEmpty(chartFieldRates)) {
        chartFieldRates = chartRow.create();
      }

      let totalCount = 0;
      for (let code in codes.counts) {
        // Exclude exam leave from calculation
        if (code === 'X') {
          continue;
        }
        totalCount += codes.counts[code];
      }

      let presentCount = codes.counts['P'] || 0;

      if (totalCount !== 0) {
        chartFieldRates.studentPresentPercents.push(presentCount / totalCount);
      } else {
        chartFieldRates.studentPresentPercents.push(0);
      }

      chartFieldRates.totalCodes += totalCount;
      chartFieldRates.L += codes.lateCount;

      data[chartField][learnerFieldValue] = chartFieldRates;
    }

    return data;
  }

  /**
   * _incrementYearLevelGenderEthnicRates adds a day code count to a given
   * bucket based on a learner's year level, gender and ethnicity.
   *
   * @param {chartData} data - the chart data object to mutate.
   * @param {codes} codes - contains a map of codes to counts.
   * @param {string} currentYearLevel - the current year level of the learner.
   * @param {string} gender - the gender label of the learner.
   * @param {string} ethnicity - the level 2 ethnicity of the learner or
   * the ATSI (if NSW).
   * @returns {chartData} - the processed chart data object.
   * @private
   */
  _incrementYearLevelGenderEthnicRates(
    data,
    codes,
    currentYearLevel,
    gender,
    ethnicity,
  ) {
    // Calculate the overall year level rates, split by gender, then
    // ethnicity.
    let yearLevelGenderEthnicRates = get(
      data,
      `yearLevelGenderEthnicRates.${currentYearLevel}.${gender}.${ethnicity}`,
    );
    if (isEmpty(yearLevelGenderEthnicRates)) {
      // Ensure the year level has been instantiated.
      let yearLevel = get(
        data,
        `yearLevelGenderEthnicRates.${currentYearLevel}`,
      );
      if (isEmpty(yearLevel)) {
        data.yearLevelGenderEthnicRates[currentYearLevel] = {};
      }

      let yearLevelGender = get(
        data,
        `yearLevelGenderEthnicRates.${currentYearLevel}.${gender}`,
      );
      if (isEmpty(yearLevelGender)) {
        data.yearLevelGenderEthnicRates[`${currentYearLevel}`][gender] = {};
      }

      // Instantiate the gender-based year level ethnic rates.
      yearLevelGenderEthnicRates = chartRow.create();
    }

    let totalCount = 0;
    for (let code in codes.counts) {
      // Exclude exam leave from calculation
      if (code === 'X') {
        continue;
      }
      totalCount += codes.counts[code];
    }

    let presentCount = codes.counts['P'] || 0;

    if (totalCount !== 0) {
      yearLevelGenderEthnicRates.studentPresentPercents.push(
        presentCount / totalCount,
      );
    } else {
      yearLevelGenderEthnicRates.studentPresentPercents.push(0);
    }

    yearLevelGenderEthnicRates.totalCodes += totalCount;
    yearLevelGenderEthnicRates.L += codes.lateCount;

    data.yearLevelGenderEthnicRates[`${currentYearLevel}`][gender][ethnicity] =
      yearLevelGenderEthnicRates;

    return data;
  }

  @action
  onChangeDateFrom(newDate) {
    this.newDateFrom = newDate;
    debounce(this, 'updateDates', 500);
  }

  @action
  onChangeDateTo(newDate) {
    this.newDateTo = newDate;
    debounce(this, 'updateDates', 500);
  }

  @action
  selectEthnicity(ethnicity) {
    this.ethnicityFilter = [...this.ethnicityFilter, ethnicity];
  }

  @action
  removeEthnicity(ethnicity) {
    this.ethnicityFilter = this.ethnicityFilter.filter(
      (item) => item !== ethnicity,
    );
  }

  @action
  selectGender(gender) {
    this.genderFilter = [...this.genderFilter, gender];
  }

  @action
  removeGender(gender) {
    this.genderFilter = this.genderFilter.filter((item) => item !== gender);
  }

  @action
  selectGroup(group) {
    this.groupIds = [...this.groupIds, group.id];
  }

  @action
  removeGroup(group) {
    this.groupIds = this.groupIds.filter((id) => id !== group.id);
  }

  @action
  selectYearLevel(year) {
    this.yearLevelFilter = [...this.yearLevelFilter, year];
  }

  @action
  removeYearLevel(year) {
    this.yearLevelFilter = this.yearLevelFilter.filter((item) => item !== year);
  }

  @action
  setSelectedTermId(term) {
    this.setProperties({
      dateFrom: term.start,
      dateTo: term.end,
      termId: term.id,
    });
  }

  // Lifecycle
  /**
   * reset provides a function to reset the controller's defaults.
   */
  reset() {
    let year = moment().year();
    this.setProperties({
      dateFrom: `${year}-01-01`,
      dateTo: `${year}-12-31`,
      newDateFrom: `${year}-01-01`,
      newDateTo: `${year}-12-31`,
      groupIds: [],
      yearLevelFilter: [],
      codeFilter: [],
      ethnicityFilter: [],
      genderFilter: [],
    });
  }
}
